import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { SelectExistingRuleModalComponent } from '../select-existing-rule-modal/select-existing-rule-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SegmentService } from '../../../../services/segments/segment-service.service';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { IEntitySegmentQuery, builderCommands } from '../segment-utilities';

@Component({
  selector: 'segment-information-form',
  standalone: true,
  imports: [InputFieldComponent, SelectExistingRuleModalComponent, ReactiveFormsModule],
  templateUrl: './segment-information-form.component.html',
  styleUrls: ['../segment-builder.css', './segment-information-form.component.css'],
})
export class SegmentInformationFormComponent implements OnInit {
  private router = inject(Router);
  private builder = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  route = inject(ActivatedRoute);
  segmentService = inject(SegmentService);
  openSelectExistingRuleModal: boolean = false;
  submitted: boolean = false;
  @Input() id!: string;

  // form
  segmentInformationForm = this.builder.group({
    segmentName: ['', Validators.required],
    description: ['', Validators.required],
  });

  // getters
  get isEditView() {
    return this.router.url.includes('/segmented-list/edit');
  }

  get routeParamId() {
    return this.route.snapshot.paramMap.get('id');
  }

  get nameError(): string {
    return this.submitted && this.segmentInformationForm.controls.segmentName.invalid ? 'Enter a name for the segment' : '';
  }

  get descriptionError(): string {
    return this.submitted && this.segmentInformationForm.controls.description.invalid
      ? 'Describe who this segment is for'
      : '';
  }

  // methods
  handleOpenSelectExistingRule() {
    this.submitted = true;
    if (this.segmentInformationForm.valid) {
      this.submitSegmentInformation();
      this.openSelectExistingRuleModal = true;
    }
  }
  handleCloseSelectExistingRule() {
    this.openSelectExistingRuleModal = false;
  }

  goToRulesPage() {
    this.submitted = true;
    if (this.segmentInformationForm.valid) {
      this.submitSegmentInformation();
      this.router.navigate(builderCommands(this.router.url, this.routeParamId), {
        queryParams: { title: 'rule' },
      });
    }
  }

  submitSegmentInformation() {
    this.segmentService.updateSegmentInformation({
      segmentName: (this.segmentInformationForm.value.segmentName ?? '').trim(),
      description: (this.segmentInformationForm.value.description ?? '').trim(),
    });
  }

  cancel() {
    this.segmentService.clearSegmentInformation();
    this.router.navigate(['organization', 'contacts', 'segmented-list']);
  }

  // lifecycle hooks
  ngOnInit() {
    if (this.segmentService.segmentInformation.segmentName) {
      this.segmentInformationForm.patchValue({
        segmentName: this.segmentService.segmentInformation.segmentName,
        description: this.segmentService.segmentInformation.description,
      });
    } else if (this.isEditView) {
      this.segmentService.getSelectedSegment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (segment: IEntitySegmentQuery | null) => {
          if (!segment) return;
          this.segmentInformationForm.patchValue({
            segmentName: segment.name,
            description: segment.description,
          });
        },
      });
    }
  }
}
